import { get } from "lodash";
import { pluralize } from "@shared/svelte/Modules/pluralize";
import * as Sentry from "@sentry/browser";

import {
  CarrotSponsoredProductAdEntity,
  DealTagEntity,
  DataScienceModelEntity,
  DualProductEntity,
  ProductCardAdEntity,
  ProductCardEntity,
  ProductCardSalesDivisionsEntity,
  ProductIndexLayoutEntity,
  CarrotShoppableUnitProductEntity,
  KevelSponsoredProductAdEntity,
} from "@shared/services/event_tracking/entities";

// Types
import type {
  Inventory,
  ProductCardInventoryStatus,
  ProductLine,
  SalesDivision,
} from "@shared/types/ProductLine";
import type { DistributorInfo, DualProductDistributor } from "@shared/types/Distributor";
import type { InventoryStatusStoreState } from "@shared/svelte/Stores/InventoryStatusStore";
import type { RetailerType } from "@shared/types/Retailer";
import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";

// Stores
import { get as getStoreValue } from "svelte/store";
import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
import InventoryStatusStore from "@shared/svelte/Stores/InventoryStatusStore";
import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";
import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";

// Services
import { StockLevels } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";

// GraphQL
import { query } from "svelte-apollo";
import {
  PRODUCT_LINE_STOCK_INFO,
  dualProductQueryBuilder,
} from "@modern/queries/stock_info/productLineStockInfo";
import { GET_SKU_STOCK_INFO } from "@modern/queries/stock_info/skuStockInfo";

export const showFeaturedLabel = (productLine: ProductLine | null): boolean => {
  const isExternalAd: boolean =
    !!productLine?.paid_tracking_values?.carrot_ad_format ||
    !!productLine?.paid_tracking_values?.is_external;

  return productLine?.is_paid && isExternalAd;
};

export const prepareSalesDivisions = (
  productLine: ProductLine | null,
  skuItemDistributorId: number[] | null = null,
): SalesDivision[] => {
  const salesDivisions = productLine?.sales_divisions || [];
  let dualDistributorIds: number[];

  if (skuItemDistributorId) {
    dualDistributorIds = skuItemDistributorId;
  } else {
    const productLineDualDistributors: DualProductDistributor[] =
      productLine?.dual_product_distributors || [];
    dualDistributorIds = productLineDualDistributors.map(d => d.id);
  }

  if (!salesDivisions.length || (dualDistributorIds?.length || 0) > 1) {
    return [];
  }

  const matchedDistributorId = productLine?.distributor_info?.distributor_id;
  const dualDistributorId = dualDistributorIds?.length ? dualDistributorIds[0] : null;

  // There are historical records with dual distributor usage that shouldn't have a matched distributor, but do.
  // If there's a dual distributor id, use that. Otherwise, use the matched distributor id.
  const finalDistributorId = dualDistributorId || matchedDistributorId;

  return salesDivisions
    .filter((sd: SalesDivision) => sd.distributorId === finalDistributorId)
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const showDealsToUser = (): boolean => {
  const currentUser: any = getStoreValue(CurrentUserStore);

  return !!currentUser && currentUser?.market?.state != "CT" && currentUser?.role != "supplier";
};

export const showProductLineDealIcon = (productLine: ProductLine): boolean => {
  return (
    showDealsToUser() &&
    (get(productLine, "distributor_info.has_discount") || get(productLine, "has_discount"))
  );
};

export const buildProductCardTrackingEntities = (
  productLine: ProductLine,
  hasDeal: boolean,
  index: number,
  productIndexLayout: string = null,
  verifiedInStock: boolean = undefined,
  stockStatus: string = undefined,
  futurePriceAvailable: boolean = undefined,
  hasVintages: boolean = undefined,
  canShowSalesDivisions: boolean = undefined, // Carousels do not show sales divisions
): any => {
  const productCardEntity = new ProductCardEntity({
    productLineId: productLine.id,
    indexPosition: index,
    isPaid: productLine.is_paid,
    priceBucket: productLine.price_bucket,
    verifiedInStock,
    stockStatus,
    futurePriceAvailable,
    hasVintages,
  });

  const trackingParams: any = [productCardEntity];

  if (productIndexLayout) {
    trackingParams.unshift(new ProductIndexLayoutEntity({ layout: productIndexLayout }));
  }

  if (productLine.model_version) {
    const modelVersionTrackingParams: DataScienceModelEntity = new DataScienceModelEntity({
      model_name: productLine.model_name,
      model_version: productLine.model_version,
    });
    trackingParams.unshift(modelVersionTrackingParams);
  }

  if (productLine.paid_tracking_values?.is_external) {
    const adParams: KevelSponsoredProductAdEntity = new KevelSponsoredProductAdEntity({
      ...productLine.paid_tracking_values,
      product_line_id: productLine.id,
    });
    trackingParams.unshift(adParams);
  } else if (productLine.paid_tracking_values?.object_tracking_id) {
    const adParams: CarrotSponsoredProductAdEntity = new CarrotSponsoredProductAdEntity({
      ...productLine.paid_tracking_values,
      product_line_id: productLine.id,
    });
    trackingParams.unshift(adParams);
  } else if (productLine.paid_tracking_values?.interaction_id) {
    const adParams: CarrotShoppableUnitProductEntity = new CarrotShoppableUnitProductEntity({
      interaction_id: productLine.paid_tracking_values.interaction_id,
      product_line_id: productLine.id,
    });
    trackingParams.unshift(adParams);
  } else if (productLine.paid_tracking_values?.campaign_id) {
    const adParams: ProductCardAdEntity = ProductCardAdEntity.fromProductLine(productLine);

    trackingParams.unshift(adParams);
  }

  if (hasDeal) {
    const dealEntity: DealTagEntity = new DealTagEntity({
      product_line_id: productLine.id,
    });
    trackingParams.unshift(dealEntity);
  }

  const dualProductDistributorLength = productLine?.dual_product_distributors?.length;
  if (dualProductDistributorLength) {
    const dualDistributorsEntity: DualProductEntity = new DualProductEntity({
      dualProductDistributors: productLine.dual_product_distributors.map(d => d.id),
    });

    trackingParams.unshift(dualDistributorsEntity);
  }

  const productCardSalesDivisions = prepareSalesDivisions(productLine);

  if (canShowSalesDivisions && productCardSalesDivisions?.length) {
    const salesDivisionsEntity: ProductCardSalesDivisionsEntity =
      ProductCardSalesDivisionsEntity.forProductCard(productCardSalesDivisions);
    trackingParams.unshift(salesDivisionsEntity);
  }

  return trackingParams;
};

export const canShowProductCardBadges = (
  distributorId: number,
  dualDistributors: { name: string }[],
): boolean => {
  return !!dualDistributors?.length || distributorId !== null;
};

export const filteredVerifiedItemsInStockCount = (
  inventoryStatuses: ProductCardInventoryStatus[],
  productInventory: Inventory[],
): number => {
  if (!inventoryStatuses) {
    return;
  }
  if (!(inventoryStatuses?.length && productInventory?.length)) {
    return 0;
  }

  const inventoryIds = productInventory.map((pi: Inventory) => pi.id);
  const filteredStatuses = inventoryStatuses?.filter((status: ProductCardInventoryStatus) => {
    return inventoryIds.includes(status.inventory_id) && status.verified_in_stock;
  });
  return filteredStatuses?.length;
};

export const showFuturePricing = (sevenFiftyIntegratedDistributor: boolean): boolean => {
  const stateSettings = getStoreValue(StateSettingsStore);
  const usesFuturePricingWithMinBottle = stateSettings
    ? stateSettings["uses_future_pricing_with_min_bottle"]
    : false;
  const usesFuturePricing = stateSettings ? stateSettings["uses_future_pricing"] : false;

  return sevenFiftyIntegratedDistributor && (usesFuturePricing || usesFuturePricingWithMinBottle);
};

export const showPostOffPricing = (sevenFiftyIntegratedDistributor: boolean): boolean => {
  const stateSettings = getStoreValue(StateSettingsStore);
  const usesFuturePricingWithMinBottle = stateSettings
    ? stateSettings["uses_future_pricing_with_min_bottle"]
    : false;

  return sevenFiftyIntegratedDistributor && usesFuturePricingWithMinBottle;
};

export const distributorIsSevenFiftyIntegrated = (
  dualDistributor: DualProductDistributor | null,
  plDistributorInfo: DistributorInfo | null,
): boolean => {
  return (
    !!(dualDistributor?.seven_fifty_integration && dualDistributor?.uses_price_zones) ||
    !!plDistributorInfo?.sevenfifty_integrated
  );
};

export const distributorIsIntegrated = (
  dualDistributor: DualProductDistributor | null,
  plDistributorInfo: DistributorInfo | null,
): boolean => {
  return (
    !!(
      dualDistributor?.integrated ||
      (dualDistributor?.seven_fifty_integration && dualDistributor?.uses_price_zones)
    ) ||
    !!plDistributorInfo?.sevenfifty_integrated ||
    !!plDistributorInfo?.integrated
  );
};

export const getBadgeText = (
  productLine: ProductLine,
  stockInfo: string | undefined = undefined,
): string => {
  if (stockInfo !== undefined) return stockInfo;

  const store: InventoryStatusStoreState = getStoreValue(InventoryStatusStore);

  if (!store.hasOwnProperty("numberOfSkusInStock") || !store.numberOfSkusInStock[productLine.id])
    return "";
  else
    return [
      store.numberOfSkusInStock[productLine.id],
      pluralize("option", store.numberOfSkusInStock[productLine.id]),
      "in stock",
    ].join(" ");
};

export const getIconType = (stockInfo: string | undefined = undefined): string | null => {
  if (stockInfo === undefined) return "CheckCircle";
  if (stockInfo.includes("in stock")) return "CheckCircle";

  switch (stockInfo) {
    case StockLevels.inStock:
      return "CheckCircle";
    case StockLevels.lowStock:
      return "Info";
    case StockLevels.outOfStock:
      return "Info";
    case StockLevels.notSoldAtThisLocation:
      return null;
    default:
      return null;
  }
};

export const getTextColor = (stockInfo: string | undefined = undefined): string => {
  if (stockInfo === undefined) return "";
  if (stockInfo.includes("in stock")) return "fizz-text-positive";

  switch (stockInfo) {
    case StockLevels.inStock:
      return "fizz-text-positive";
    case StockLevels.lowStock:
      return "fizz-text-warning";
    case StockLevels.outOfStock:
      return "fizz-text-negative";
    case StockLevels.notSoldAtThisLocation:
      return "fizz-text-subdued";
    default:
      return "fizz-text-subdued";
  }
};

type ProductLineStockInfoQueryResult = {
  getSkuGroup: {
    overallStockStatus: string;
    numOptionsInStock?: number;
  };
};

const buildQueryVariables = (skus, distributorId, accountNumber, stockSiteId) => {
  const retailerStore: RetailerType = getStoreValue(CurrentRetailerStore);
  return {
    distributorId: distributorId.toString(),
    stockLevelsInput: {
      skus,
      stockSiteIds: stockSiteId ? [stockSiteId] : [],
      eligibility: {
        customerId: accountNumber,
        channels: [retailerStore.license_type],
        locationInfo: {
          county: retailerStore.county_name,
          postalCode: retailerStore.zip,
          state: retailerStore.state,
        },
      },
    },
  };
};

export const getProductLineStockInfo = async (
  inventory: Inventory[],
  distributorId: number,
  accountNumber: string,
  fulfillmentMethod: RetailerFulfillmentMethod = null,
  defaultStockSiteId: string | null = null,
) => {
  const skus = inventory?.reduce((acc, i) => {
    if (i?.distributor_id === distributorId) acc.push(i.sku);
    return acc;
  }, []);
  const stockSiteId: string | null =
    fulfillmentMethod?.warehouse?.location_code || defaultStockSiteId;
  const formattedStockSiteId: string | null =
    stockSiteId && stockSiteId?.startsWith("VIP_") ? stockSiteId.substring(4) : stockSiteId;
  const variables = buildQueryVariables(skus, distributorId, accountNumber, formattedStockSiteId);

  try {
    const response = query<ProductLineStockInfoQueryResult>(PRODUCT_LINE_STOCK_INFO, { variables });

    const stockInfoData = await response?.result();
    return formattedStockInfo(stockInfoData);
  } catch (error) {
    Sentry.captureException(error);
    return StockLevels.temporarilyUnavailable;
  }
};

export const formattedStockInfo = (stockInfoData: any): string | undefined => {
  if (!stockInfoData) return;
  if (stockInfoData?.loading) return "";
  return stockInfoData?.error
    ? StockLevels.temporarilyUnavailable
    : stockInfoData?.data?.getSkuGroup?.overallStockStatus;
};

export const getDualProductStockInfo = async (
  inventory: Inventory[],
  dualDistributors: DualProductDistributor[],
  overrideDistributorId: number | null = null,
) => {
  if (overrideDistributorId) {
    const distributor = dualDistributors.find(d => d.id === overrideDistributorId);
    return await getProductLineStockInfo(
      inventory,
      overrideDistributorId,
      distributor?.account_number,
    );
  }
  let variables = {};
  const dualProductAttributes = dualDistributors.map((distributor, index) => {
    const distributorId = distributor.id.toString();
    const skus = inventory?.reduce((acc, i) => {
      if (i?.distributor_id?.toString() === distributorId) acc.push(i.sku);
      return acc;
    }, []);

    if (skus.length > 0) {
      const distributorVariables = buildQueryVariables(
        skus,
        distributorId,
        distributor.account_number,
        null,
      );

      const distributorParam = `distributorId${index}`;
      const stockLevelsInputParam = `stockLevelsInput${index}`;
      variables[distributorParam] = distributorId;
      variables[stockLevelsInputParam] = distributorVariables.stockLevelsInput;

      return distributorVariables;
    }
  });

  try {
    const response = query(dualProductQueryBuilder(dualProductAttributes), { variables });
    const stockInfoData = await response?.result();
    return dualProductFormattedStockInfo(stockInfoData, overrideDistributorId);
  } catch (error) {
    Sentry.captureException(error);
    return StockLevels.temporarilyUnavailable;
  }
};

export const dualProductFormattedStockInfo = (
  stockInfoData: any,
  overrideDistributorId: number | null,
): string | undefined => {
  if (overrideDistributorId) return formattedStockInfo(stockInfoData);

  if (!stockInfoData) return;
  if (stockInfoData.loading) return "";
  if (stockInfoData.error) return StockLevels.temporarilyUnavailable;

  const queryResults: any = Object.values(stockInfoData.data);
  if (queryResults.every(data => data.overallStockStatus === "")) return "";
  if (queryResults.every(data => data.overallStockStatus === StockLevels.inStock))
    return StockLevels.inStock;
  if (queryResults.every(data => data.overallStockStatus === StockLevels.outOfStock))
    return StockLevels.outOfStock;
  if (queryResults.every(data => data.overallStockStatus === StockLevels.lowStock))
    return StockLevels.lowStock;
  let num = 0;
  for (let result of queryResults) {
    if (result?.numOptionsInStock) {
      num += result.numOptionsInStock;
    }
  }
  return `${num} options in stock`;
};

type GetStockLevelsQueryResult = {
  getStockLevels: {
    status: string;
    reason: string;
  }[];
};

export const isAccountNumberRequired = async (
  distributorId: number,
  sku: string,
  stockSiteId: string | null,
  accountNumber: string,
) => {
  if (!distributorId || !sku) return false;

  const fulfillmentMethod = getStoreValue(RetailerFulfillmentMethodsStore)[distributorId];
  const variables = buildQueryVariables(
    [sku],
    distributorId,
    accountNumber,
    stockSiteId || fulfillmentMethod?.warehouse?.location_code || null,
  );
  try {
    const response = query<GetStockLevelsQueryResult>(GET_SKU_STOCK_INFO, { variables });
    const stockLevelsData = await response?.result();
    if (!stockLevelsData?.data?.getStockLevels) return false;
    return (
      stockLevelsData?.data?.getStockLevels[0]?.status === "UNKNOWN" &&
      stockLevelsData?.data?.getStockLevels[0]?.reason === "CUSTOMER_ID_REQUIRED"
    );
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
};
